/**
 * `FilesViewer`, the per-request tag that says who is looking (spec R8).
 *
 * It exists so that no service input widens. `assertCanAccess` keeps its
 * `{ userId, organizationId, nodeId?, scope?, intent }` at all twenty call sites
 * and reads the rest from here, rather than every caller learning to pass an
 * admin flag and a team list it has no reason to know about.
 *
 * `isOrgAdmin` is `AuthorisedIdentity.role === 'admin'`, DELIBERATELY EXCLUDING
 * `superadmin`. A super-admin does not get manage over a broker's personal
 * folders; Cova staff reach an org's files through the super-admin browser,
 * which has its own audit.
 *
 * `teamIds` come from `team_memberships` joined to `teams` on the org, because
 * memberships carry no organisation of their own.
 *
 * Provided together with the ledger's `AuditActor` by the same providers, so a
 * runtime cannot supply one and forget the other. The agent runtime fills it once per
 * session; a webhook intake supplies an empty viewer, so intake writes reach
 * client scope only.
 */

import { Context, Effect, Schema as S } from "effect"

import { OrgId } from "@/domain/shared/branded-types"

export const FilesViewerShape = S.Struct({
  userId: S.String,
  isOrgAdmin: S.Boolean,
  teamIds: S.Array(S.String),
  /**
   * The organisation this viewer looks at, when the lane knows it (cova_audit
   * P5a). `FilesViewerResolver` always sets it, so the RPC lane and every agent
   * product that resolves its viewer carry it; the hand-built lanes set it
   * when their caller passes one. `provideFilesRequestContext` binds the
   * ledger's `AuditActor` for THIS organisation, so a lane with none binds no
   * actor, and a writer it reaches stops on the missing service rather than
   * logging to a guessed chain.
   */
  organizationId: S.optional(OrgId),
}).annotations({
  identifier: "FilesViewer",
  title: "Files viewer",
})
export type FilesViewerShape = S.Schema.Type<typeof FilesViewerShape>

/**
 * The two folder sets a viewer reaches by a grant, from
 * `NodeGrantsRepository.resolveAccessSets`. `granted` is every folder this
 * viewer or one of their teams holds any grant on; `editable` is the subset
 * held as `editor`.
 */
export interface FilesAccessSets {
  readonly granted: ReadonlyArray<string>
  readonly editable: ReadonlyArray<string>
}

/**
 * THE ONE FIELD THE SHAPE ABOVE CANNOT CARRY, and the reason it sits here
 * rather than in `FilesViewerShape` (spec section 4.3).
 *
 * The grants a viewer holds are resolved ONCE PER REQUEST and then read by
 * every predicate in that request. A plain array on the shape would make every
 * provider resolve them eagerly, including the many requests that never touch a
 * restricted folder; a memo on the singleton service would share one viewer's
 * grants with the next request on the same instance, which is the one thing a
 * per-request tag exists to prevent.
 *
 * So the memo lives ON THE PROVIDED VALUE: the provider builds this field with
 * `Effect.cached`, which resolves on first use and replays the same result for
 * the life of that one value. One request resolves once; two requests never
 * share.
 *
 * OPTIONAL, AND FAIL-CLOSED WHEN ABSENT. A provider written before this field
 * existed supplies a viewer without it, and `FilesAuthorisationService` then
 * resolves the sets itself for that request rather than assuming an empty set
 * quietly or a full one dangerously. The error channel is `unknown` because the
 * resolution is a repository read and a domain file names no repository error;
 * the authorisation service maps it at the boundary where it is consumed.
 */
export interface FilesViewerValue extends FilesViewerShape {
  readonly accessSets?: Effect.Effect<FilesAccessSets, unknown>
  /**
   * Whether this viewer reaches one client, asked of a node in client scope by
   * the single-node checks (`assertCanAccess`, `assertCanManage`). A client's
   * tree is reached through the client's own access list, which the pages
   * carry in their statements; a check on one node carries it here, so a node
   * id of a client the viewer cannot reach is refused rather than opened.
   *
   * `FilesViewerResolver` fills it on every lane that resolves a person (the
   * RPC middleware, the agent products, the agent RPC handler). A lane that
   * builds its viewer by hand for no person, or for a named client it has
   * already checked (intake, a pipeline, a script, the file-extractions copy),
   * leaves it out and keeps that behaviour. A failed read is a refusal. The
   * error channel is `unknown` for the reason `accessSets`'s is.
   */
  readonly reachesClient?: (clientId: string) => Effect.Effect<boolean, unknown>
}

export class FilesViewer extends Context.Tag("FilesViewer")<
  FilesViewer,
  FilesViewerValue
>() {}
